import React, { useState, useEffect } from 'react';
import { 
  Plus, Pencil, Trash2, GripVertical, AlertCircle,
  ArrowUp, ArrowDown, Check, X
} from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '../../utils/cn';
import { useCompany } from '../../contexts/CompanyContext';
import toast from 'react-hot-toast';

interface City {
  id: string;
  name: string;
  order: number;
}

interface SortableCityItemProps {
  city: City;
  onEdit: (city: City) => void;
  onDelete: (id: string) => void;
}

function SortableCityItem({ city, onEdit, onDelete }: SortableCityItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: city.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm"
    >
      <button
        className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-5 w-5" />
      </button>
      
      <span className="flex-1 font-medium text-gray-900">{city.name}</span>
      
      <div className="flex items-center gap-2">
        <button
          onClick={() => onEdit(city)}
          className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
          title="Editar"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          onClick={() => onDelete(city.id)}
          className="p-1 text-red-600 hover:text-red-800 transition-colors"
          title="Excluir"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function CitiesManagementPage() {
  const { company } = useCompany();
  const [cities, setCities] = useState<City[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [newCityName, setNewCityName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Load cities from localStorage
    const storedCities = localStorage.getItem('cities');
    if (storedCities) {
      setCities(JSON.parse(storedCities));
    } else {
      // Set default cities if none stored
      const defaultCities = [
        { id: 'anapolisgo', name: 'Anápolis', order: 0 },
        { id: 'rioverdego', name: 'Rio Verde', order: 1 },
        { id: 'itumbiara', name: 'Itumbiara', order: 2 }
      ];
      setCities(defaultCities);
      localStorage.setItem('cities', JSON.stringify(defaultCities));
    }
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const updateCities = (newCities: City[]) => {
    setCities(newCities);
    localStorage.setItem('cities', JSON.stringify(newCities));
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setCities((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        const newItems = arrayMove(items, oldIndex, newIndex).map((item, index) => ({
          ...item,
          order: index
        }));

        localStorage.setItem('cities', JSON.stringify(newItems));
        return newItems;
      });
    }
  };

  const handleAddCity = () => {
    if (!newCityName.trim()) {
      setError('O nome da cidade é obrigatório');
      return;
    }

    const cityExists = cities.some(
      city => city.name.toLowerCase() === newCityName.trim().toLowerCase()
    );

    if (cityExists) {
      setError('Esta cidade já está cadastrada');
      return;
    }

    const newCity: City = {
      id: newCityName.toLowerCase().replace(/\s+/g, ''),
      name: newCityName.trim(),
      order: cities.length
    };

    const newCities = [...cities, newCity];
    updateCities(newCities);
    setNewCityName('');
    setShowForm(false);
    setError(null);

    toast.success('Cidade adicionada com sucesso!', {
      duration: 3000,
      position: 'top-right'
    });
  };

  const handleEditCity = () => {
    if (!editingCity || !newCityName.trim()) {
      setError('O nome da cidade é obrigatório');
      return;
    }

    const cityExists = cities.some(
      city => city.id !== editingCity.id && 
      city.name.toLowerCase() === newCityName.trim().toLowerCase()
    );

    if (cityExists) {
      setError('Esta cidade já está cadastrada');
      return;
    }

    const newCities = cities.map(city => 
      city.id === editingCity.id 
        ? { ...city, name: newCityName.trim() }
        : city
    );

    updateCities(newCities);
    setEditingCity(null);
    setNewCityName('');
    setError(null);

    toast.success('Cidade atualizada com sucesso!', {
      duration: 3000,
      position: 'top-right'
    });
  };

  const handleDeleteCity = (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta cidade?')) return;

    const newCities = cities.filter(city => city.id !== id);
    updateCities(newCities);

    toast.success('Cidade excluída com sucesso!', {
      duration: 3000,
      position: 'top-right'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Gestão de Cidades</h1>
          <p className="mt-1 text-gray-500">
            Gerencie as cidades disponíveis na plataforma
          </p>
        </div>
        {!showForm && !editingCity && (
          <button
            onClick={() => setShowForm(true)}
            className={cn(
              "flex items-center gap-2 rounded-lg px-4 py-2 text-white",
              "transition-all hover:opacity-90 active:scale-95 bg-[#7c3aed]"
            )}
          >
            <Plus className="h-4 w-4" />
            Nova Cidade
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {(showForm || editingCity) && (
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {editingCity ? 'Editar Cidade' : 'Nova Cidade'}
          </h2>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={newCityName}
                onChange={(e) => setNewCityName(e.target.value)}
                placeholder="Nome da cidade"
                className={cn(
                  "block w-full rounded-lg border border-gray-300 px-4 py-2.5",
                  "focus:border-purple-500 focus:ring-purple-500"
                )}
              />
            </div>
            <button
              onClick={editingCity ? handleEditCity : handleAddCity}
              className={cn(
                "flex items-center gap-2 rounded-lg px-4 py-2.5 text-white",
                "transition-all hover:opacity-90 active:scale-95 bg-green-600"
              )}
            >
              <Check className="h-4 w-4" />
              {editingCity ? 'Salvar' : 'Adicionar'}
            </button>
            <button
              onClick={() => {
                setShowForm(false);
                setEditingCity(null);
                setNewCityName('');
                setError(null);
              }}
              className={cn(
                "flex items-center gap-2 rounded-lg px-4 py-2.5",
                "text-gray-700 border border-gray-300",
                "transition-all hover:bg-gray-50 active:scale-95"
              )}
            >
              <X className="h-4 w-4" />
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={cities}
            strategy={verticalListSortingStrategy}
          >
            {cities.map((city) => (
              <SortableCityItem
                key={city.id}
                city={city}
                onEdit={(city) => {
                  setEditingCity(city);
                  setNewCityName(city.name);
                }}
                onDelete={handleDeleteCity}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}